import { Routes, Route } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { RoleRoute } from './components/layout/RoleRoute';

// Pages
import { LoginPage } from './pages/auth/LoginPage';
import { PasswordRecoveryPage } from './pages/auth/PasswordRecoveryPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { ProjectListPage } from './pages/projects/ProjectListPage';
import { ProjectDetailPage } from './pages/projects/ProjectDetailPage';
import { OrdenTrabajoPage } from './pages/projects/OrdenTrabajoPage';
import { CreateOtAutoPage } from './pages/projects/CreateOtAutoPage';
import { NuevoProyectoPage } from './pages/projects/NuevoProyectoPage';
import { MedidasPage } from './pages/projects/MedidasPage';
import { CreateMedidaSetAutoPage } from './pages/projects/CreateMedidaSetAutoPage';
import { ProformaListPage } from './pages/proformas/ProformaListPage';
import { ProformaBuilderPage } from './pages/proformas/ProformaBuilderPage';
import { ProformaPreviewPage } from './pages/proformas/ProformaPreviewPage';
import { CreateProformaAutoPage } from './pages/proformas/CreateProformaAutoPage';
import { PlanillaListPage } from './pages/planillas/PlanillaListPage';
import { PlanillasProjectPage } from './pages/planillas/PlanillasProjectPage';
import { NuevaPlanillaPage } from './pages/planillas/NuevaPlanillaPage';
import { AdminReviewPage } from './pages/planillas/AdminReviewPage';
import { GerenteApprovalPage } from './pages/planillas/GerenteApprovalPage';
import { PlanillaRedirectPage } from './pages/planillas/PlanillaRedirectPage';
import { PedidosPage } from './pages/pedidos/PedidosPage';
import { NuevoPedidoPage } from './pages/pedidos/NuevoPedidoPage';
import { DespachosPage } from './pages/compras/DespachosPage';
import { ComprasPedidoDetailPage } from './pages/compras/ComprasPedidoDetailPage';
import { ComprasReviewPage } from './pages/compras/ComprasReviewPage';
import { RecetasPage } from './pages/recetas/RecetasPage';
import { MaestrosPage } from './pages/maestros/MaestrosPage';
import { ClientListPage } from './pages/clients/ClientListPage';
import { AvanceDeObraPage } from './pages/avance/AvanceDeObraPage';
import { ReportesPage } from './pages/reportes/ReportesPage';
import { LiquidacionPage } from './pages/liquidacion/LiquidacionPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { ContabilidadPage } from './pages/contabilidad/ContabilidadPage';

const ADMIN_ROLES = ['gerente', 'operaciones', 'administrativo'] as const;
const ALL_ROLES = ['gerente', 'operaciones', 'administrativo', 'residente', 'compras', 'contabilidad'] as const;

export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/recuperar-password" element={<PasswordRecoveryPage />} />

      {/* Protected routes with layout */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                {/* Dashboard — all roles */}
                <Route
                  path="/"
                  element={
                    <RoleRoute allowedRoles={[...ALL_ROLES]}>
                      <DashboardPage />
                    </RoleRoute>
                  }
                />

                {/* Proyectos */}
                <Route
                  path="/proyectos"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente', 'compras']}>
                      <ProjectListPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proyectos/nuevo"
                  element={
                    <RoleRoute allowedRoles={['administrativo', 'gerente', 'operaciones', 'residente']}>
                      <NuevoProyectoPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proyectos/:id"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente', 'compras']}>
                      <ProjectDetailPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proyectos/:id/medidas"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente']}>
                      <MedidasPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proyectos/:id/nueva-medida"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente']}>
                      <CreateMedidaSetAutoPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proyectos/:id/nueva-proforma"
                  element={
                    <RoleRoute allowedRoles={ADMIN_ROLES}>
                      <CreateProformaAutoPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proyectos/:id/pedidos"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente', 'compras']}>
                      <PedidosPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proyectos/:id/pedidos/nuevo"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente']}>
                      <NuevoPedidoPage />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/proyectos/:id/planillas"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente']}>
                      <PlanillasProjectPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proyectos/:id/planillas/nueva"
                  element={
                    <RoleRoute allowedRoles={['residente', 'gerente', 'operaciones']}>
                      <NuevaPlanillaPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proyectos/:id/planillas/:planillaId"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente']}>
                      <NuevaPlanillaPage />
                    </RoleRoute>
                  }
                />

                {/* Smart planilla redirect — resolves correct page by role + state (used by notifications) */}
                <Route
                  path="/planillas/:planillaId"
                  element={<PlanillaRedirectPage />}
                />

                {/* Admin planilla review */}
                <Route
                  path="/planillas/:planillaId/revision"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES]}>
                      <AdminReviewPage />
                    </RoleRoute>
                  }
                />

                {/* Gerente planilla approval */}
                <Route
                  path="/planillas/:planillaId/aprobacion"
                  element={
                    <RoleRoute allowedRoles={['gerente', 'operaciones']}>
                      <GerenteApprovalPage />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/proyectos/:id/crear-ot"
                  element={
                    <RoleRoute allowedRoles={ADMIN_ROLES}>
                      <CreateOtAutoPage />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/proyectos/:id/orden-trabajo"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente']}>
                      <OrdenTrabajoPage />
                    </RoleRoute>
                  }
                />

                {/* Clientes */}
                <Route
                  path="/clientes"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES]}>
                      <ClientListPage />
                    </RoleRoute>
                  }
                />

                {/* Proformas — C-01: Global tab */}
                <Route
                  path="/proformas"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES]}>
                      <ProformaListPage />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/proformas/:id"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente']}>
                      <ProformaBuilderPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/proformas/:id/preview"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES]}>
                      <ProformaPreviewPage />
                    </RoleRoute>
                  }
                />

                {/* Recetas — C-02: APU catalog */}
                <Route
                  path="/recetas"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES, 'residente']}>
                      <RecetasPage />
                    </RoleRoute>
                  }
                />

                {/* Planillas — C-03: Cross-project access */}
                <Route
                  path="/planillas"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES]}>
                      <PlanillaListPage />
                    </RoleRoute>
                  }
                />

                {/* Compras review — planilla descuentos */}
                <Route
                  path="/compras/planillas/:planillaId/descuentos"
                  element={
                    <RoleRoute allowedRoles={['compras']}>
                      <ComprasReviewPage />
                    </RoleRoute>
                  }
                />

                {/* Despachos */}
                <Route
                  path="/despachos"
                  element={
                    <RoleRoute allowedRoles={['gerente', 'operaciones', 'administrativo', 'compras']}>
                      <DespachosPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/compras/pedidos/:pedidoId"
                  element={
                    <RoleRoute allowedRoles={['compras', 'gerente', 'operaciones', 'administrativo']}>
                      <ComprasPedidoDetailPage />
                    </RoleRoute>
                  }
                />

                {/* Avance de Obra */}
                <Route
                  path="/avance-de-obra"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES]}>
                      <AvanceDeObraPage />
                    </RoleRoute>
                  }
                />

                {/* Reportes */}
                <Route
                  path="/reportes"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES]}>
                      <ReportesPage />
                    </RoleRoute>
                  }
                />

                {/* Maestros */}
                <Route
                  path="/maestros"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES]}>
                      <MaestrosPage />
                    </RoleRoute>
                  }
                />

                {/* Perfil — all roles */}
                <Route
                  path="/perfil"
                  element={
                    <RoleRoute allowedRoles={[...ALL_ROLES]}>
                      <ProfilePage />
                    </RoleRoute>
                  }
                />

                {/* Liquidación */}
                <Route
                  path="/liquidacion"
                  element={
                    <RoleRoute allowedRoles={[...ADMIN_ROLES]}>
                      <LiquidacionPage />
                    </RoleRoute>
                  }
                />

                {/* Contabilidad */}
                <Route
                  path="/contabilidad"
                  element={
                    <RoleRoute allowedRoles={['gerente', 'operaciones', 'administrativo', 'contabilidad']}>
                      <ContabilidadPage />
                    </RoleRoute>
                  }
                />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
